import type {WizardData} from '../index'
import type {
  AiAllocationContext,
  AllocationScope,
  LessonPlanningData,
} from '../types/teacherAllocationAi'
import {buildAllocationSlotKey, getClassesInScope, getTeacherFullName} from './allocationIds'
import {curriculumFieldsFor} from './levelCurriculum'

export type BuildAllocationContextParams = {
  manualData: WizardData['manualData']
  lessonPlanningData: LessonPlanningData
  scope: AllocationScope
  activeFilters: {branch?: string; level?: string; curriculum?: string}
  normalizeBranchName: (branch?: string) => string
  getSubjectsForClass: (classItem: any) => string[]
  getLessonClassKey: (className: string, branch?: string, curriculum?: string) => string
  isSlotDeleted: (classKey: string, subjectName: string) => boolean
}

export function buildAllocationContext(params: BuildAllocationContextParams): AiAllocationContext {
  const {
    manualData,
    lessonPlanningData,
    scope,
    activeFilters,
    normalizeBranchName,
    getSubjectsForClass,
    getLessonClassKey,
    isSlotDeleted,
  } = params

  const teachers = manualData.teachers ?? []
  const classes = manualData.classes ?? []
  const scopedClasses = getClassesInScope(classes, scope, activeFilters, normalizeBranchName)

  const contextTeachers = teachers
    .map((t: any) => {
      const name = getTeacherFullName(t)
      if (!name) return null
      const employeeId = String(t?.employeeId ?? '').trim() || name
      return {employeeId, name}
    })
    .filter((t): t is AiAllocationContext['teachers'][0] => Boolean(t))

  const contextClasses = scopedClasses
    .map((c: any) => {
      const name = String(c?.name ?? '').trim()
      if (!name) return null
      const classKey = getLessonClassKey(name, c?.branch, c?.curriculum)
      const curriculum = String(c?.curriculum ?? '').trim()
      // Codes let the assistant match "FBISE Grade 1" / "Grade 3 (FBISE)" to the right class.
      const {curriculum_code, curriculum_short_code} = curriculumFieldsFor(
        curriculum,
        manualData,
        true
      )
      return {
        classKey,
        name,
        branch: String(c?.branch ?? '').trim(),
        level: String(c?.level ?? '').trim(),
        curriculum,
        ...(curriculum_code ? {curriculum_code} : {}),
        ...(curriculum_short_code ? {curriculum_short_code} : {}),
      }
    })
    .filter((c): c is AiAllocationContext['classes'][0] => Boolean(c))

  const subjects: AiAllocationContext['subjects'] = []
  const grid: AiAllocationContext['grid'] = {}

  scopedClasses.forEach((cls: any) => {
    const className = String(cls?.name ?? '').trim()
    if (!className) return

    const classKey = getLessonClassKey(className, cls?.branch, cls?.curriculum)
    getSubjectsForClass(cls).forEach((subjectName) => {
      if (!subjectName || isSlotDeleted(classKey, subjectName)) return

      const cell = lessonPlanningData[classKey]?.[subjectName]
      const assignedTeacher = String(cell?.teacher ?? '').trim() || null
      const teacherEmployeeId = String(cell?.teacherEmployeeId ?? '').trim() || null

      subjects.push({
        slotKey: buildAllocationSlotKey(classKey, subjectName),
        name: subjectName,
        classKey,
        className,
        assignedTeacher,
        teacherEmployeeId,
      })

      if (cell) {
        if (!grid[classKey]) grid[classKey] = {}
        grid[classKey][subjectName] = {
          teacher: cell.teacher ?? '',
          frequency: cell.frequency ?? '',
          duration: cell.duration ?? '',
          ...(cell.teacherEmployeeId ? {teacherEmployeeId: cell.teacherEmployeeId} : {}),
        }
      }
    })
  })

  return {
    teachers: contextTeachers,
    classes: contextClasses,
    subjects,
    grid,
  }
}
